import React, { useMemo, useState } from 'react'
import RELEASES from 'virtual:releases'
import { RELEASE_TAGS, TAG_ORDER } from '../releaseTags'

export function ReleasesPage() {
  const [activeTag, setActiveTag] = useState(null)

  const availableTags = useMemo(() => {
    const seen = new Set()
    for (const r of RELEASES) for (const i of r.items) for (const t of i.tags) seen.add(t)
    return TAG_ORDER.filter((t) => seen.has(t))
  }, [])

  const filtered = useMemo(() => {
    if (!activeTag) return RELEASES
    return RELEASES.map((r) => ({
      ...r,
      items: r.items.filter((i) => i.tags.includes(activeTag)),
    })).filter((r) => r.items.length > 0)
  }, [activeTag])

  return (
    <div>
      <h2 style={styles.heading}>What's new</h2>
      <p style={styles.subtitle}>Recent updates and new features in Soundcheck.</p>
      {availableTags.length > 0 && (
        <div style={styles.filters} role="group" aria-label="Filter by tag">
          <FilterPill
            label="All"
            active={activeTag === null}
            onClick={() => setActiveTag(null)}
          />
          {availableTags.map((t) => (
            <FilterPill
              key={t}
              label={RELEASE_TAGS[t].label}
              bg={RELEASE_TAGS[t].bg}
              fg={RELEASE_TAGS[t].fg}
              active={activeTag === t}
              onClick={() => setActiveTag(activeTag === t ? null : t)}
            />
          ))}
        </div>
      )}
      <div style={styles.list}>
        {filtered.map((release) => (
          <div key={release.version} style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.version}>v{release.version}</span>
              <span style={styles.date}>{release.date}</span>
            </div>
            <h3 style={styles.cardTitle}>{release.title}</h3>
            <ul style={styles.items}>
              {release.items.map((item, i) => (
                <li key={i} style={styles.item}>
                  <span dangerouslySetInnerHTML={{ __html: item.html }} />
                  {item.tags.map((t) => {
                    const meta = RELEASE_TAGS[t]
                    if (!meta) return null
                    return (
                      <span
                        key={t}
                        style={{ ...styles.tagPill, background: meta.bg, color: meta.fg }}
                      >
                        {meta.label}
                      </span>
                    )
                  })}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

function FilterPill({ label, bg, fg, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...styles.filterPill,
        background: active ? bg || '#111827' : 'transparent',
        color: active ? fg || '#fff' : 'var(--text-secondary)',
        borderColor: active ? 'transparent' : 'var(--border)',
      }}
      aria-pressed={active}
    >
      {label}
    </button>
  )
}

const styles = {
  heading: { fontSize: '18px', fontWeight: 700, margin: '0 0 4px' },
  subtitle: { fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 12px' },
  filters: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' },
  filterPill: {
    fontSize: '11px',
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: '999px',
    border: '1px solid var(--border)',
    cursor: 'pointer',
  },
  list: { display: 'flex', flexDirection: 'column', gap: '16px' },
  card: {
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '16px 20px',
    background: 'var(--card-bg, #fff)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  version: { fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' },
  date: { fontSize: '11px', color: 'var(--text-secondary)' },
  cardTitle: { fontSize: '15px', fontWeight: 600, margin: '0 0 8px' },
  items: { margin: 0, paddingLeft: '18px' },
  item: { fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 },
  tagPill: {
    display: 'inline-block',
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 7px',
    marginLeft: '6px',
    borderRadius: '999px',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
}
